import { FC, ReactElement, ReactNode } from 'react';
import { Typography } from '@mui/material';

import { PassengerPriceInfoAggregateView, PriceType } from '@mopla/data-models';
import { PriceString } from '@mopla/ui';

import { PriceRow } from './styled';

type TRender = (parts: {
	label: ReactNode;
	price: ReactElement;
	dataTestId?: string;
}) => ReactElement;

export interface IBookingTotalPriceProps {
	/** Booking-level aggregate: the backend states which price types to render. */
	priceInfo?: PassengerPriceInfoAggregateView | null;
	label: ReactNode;
	render?: TRender;
	dataTestId?: string;
}

const renderTotalRow: TRender = ({ label, price, dataTestId }) => (
	<PriceRow>
		<Typography variant="h4-sb">{label}</Typography>
		<Typography variant="h4-sb" data-testid={dataTestId}>
			{price}
		</Typography>
	</PriceRow>
);

/**
 * Single-row total: an amount, the ÖPNV tariff, the free-ride wording, or any
 * combination the backend sends. Renders nothing when it sends no price type,
 * so the caller gets no empty row.
 */
export const BookingTotalPrice: FC<IBookingTotalPriceProps> = ({
	priceInfo,
	label,
	render = renderTotalRow,
	dataTestId,
}) => {
	const priceTypes = priceInfo?.priceTypes ?? [];

	if (!priceTypes.length) {
		return null;
	}

	/** An amount is due even when it sums up to zero — 0,00 € is a valid total. */
	const hasAmount = priceTypes.includes(PriceType.MOPLA);

	return render({
		label,
		dataTestId,
		price: (
			<PriceString
				price={hasAmount ? priceInfo?.totalCostCents ?? 0 : null}
				isFree={priceTypes.includes(PriceType.FREE)}
				hasPriceTariff={priceTypes.includes(PriceType.TARIFF)}
				i18nVersion="V2"
			/>
		),
	});
};
